<template>
  <VueFinalModal
    v-model="isOpen"
    class="modal-bottomScreen"
  >
    <div class="modal-content modal-accommodations-content">
      <button
        type="button"
        class="modal-close"
        @click="$modal.hide('studentAccommodationsModal')"
      >
        <em class="icon-close"></em>
      </button>
      <div class="modal-body mx-auto">
        <div class="modal-title">
          <h1 class="mb-4">Student Accommodations</h1>
        </div>
        <div v-if="studentInfo" class="student-info-section">
          <h3 class="student-name mb-4">
            {{ studentInfo.last_name }}, {{ studentInfo.first_name }}
          </h3>
          
          <div class="accommodations-form">
            <div class="form-section mb-4">
              <h4 class="section-title">End Date Accommodation</h4>
              <div class="current-date-display mb-3">
                <label class="form-label">Current End Date:</label>
                <div class="current-date-value">
                  {{ displayEndDate }}
                </div>
              </div>
              
              <div class="accommodation-explanation mb-3">
                <p class="explanation-text">
                  Adjusting the date below will extend the student's access beyond the course or section end date.
                </p>
              </div>
              
              <div class="form-group">
                <label for="newEndDate" class="form-label">New End Date:</label>
                <input
                  id="newEndDate"
                  v-model="newEndDate"
                  type="date"
                  class="form-control"
                  placeholder="Select new end date"
                />
              </div>
            </div>
            
            <div class="form-actions d-flex justify-content-between">
              <button 
                type="button" 
                class="btn default"
                @click="$modal.hide('studentAccommodationsModal')"
              >
                Cancel
              </button>
              <button 
                type="button" 
                class="btn primary"
                :disabled="!newEndDate || saving"
                @click="updateEndDate"
              >
                {{ saving ? 'Updating...' : 'Update End Date' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script>
import api from '@/services/api.js';
import { getModalState } from '@/plugins/modal-compat';

export default {
  name: "StudentAccommodationsModal",
  computed: {
    isOpen: {
      get() { return getModalState('studentAccommodationsModal').isOpen.value; },
      set(val) { getModalState('studentAccommodationsModal').isOpen.value = val; }
    },
    modalParams() { return getModalState('studentAccommodationsModal').params.value; },
    displayEndDate() {
      const date = this.currentEndDate || this.courseEndDate;
      return date ? this.formatDate(date) : 'Not set';
    }
  },
  watch: {
    modalParams(newParams) {
      if (newParams) { this.beforeOpen({ params: newParams }); }
    }
  },
  data() {
    return {
      studentInfo: null,
      userSectionId: null,
      sectionId: null,
      currentEndDate: null,
      courseEndDate: null,
      newEndDate: null,
      saving: false
    }
  },
  methods: {
    beforeOpen(event) {
      this.userSectionId = event.params.userSectionID;
      this.sectionId = event.params.section_id || event.params.sectionId;
      this.studentInfo = {
        first_name: event.params.user_first_name,
        last_name: event.params.user_last_name
      };
      this.currentEndDate = event.params.accommodated_end_date;
      this.courseEndDate = event.params.course_end_date;
    },
    formatDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      
      // Check if the date string includes time information
      const hasTime = dateString.includes('T') || dateString.includes(' ');
      
      if (hasTime) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }
      
      // For date-only strings, show with assumed end-of-day time
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) + ' at 11:59 PM';
    },
    async updateEndDate() {
      if (!this.newEndDate) {
        alert('Please select a new end date');
        return;
      }
      
      this.saving = true;
      
      try {
        // Create a Date object from the input and set time to 11:59:59 PM
        const endDate = new Date(this.newEndDate + 'T23:59:59');
        
        const data = {
          user_section: {
            accommodated_end_date: endDate.toISOString()
          }
        };
        
        await api.updateUserSection(this.userSectionId, data);
        
        // Update the current end date with the new value
        this.currentEndDate = this.newEndDate;
        
        // Reset the form
        this.newEndDate = null;
        
        // Show success message or handle success as needed
        alert('End date updated successfully!');
        
        // Optionally close the modal
        this.$modal.hide('studentAccommodationsModal');
        
      } catch (error) {
        console.error('Error updating end date:', error);
        alert('Error updating end date. Please try again.');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-accommodations-content {
  width: 650px;
  margin: 0 auto;
  padding: 15px;
  
  .modal-close {
    margin: 10px 15px 0 auto;
    background-color: transparent;
    border: 0;
    font-size: 18px;
    outline: 0;
  }
  
  .modal-body {
    padding: 0;
  }
  
  .modal-title {
    h1 {
      font-weight: bold;
      font-size: 24px;
      color: #272c34;
      margin-bottom: 15px;
    }
  }
  
  .student-name {
    font-weight: 600;
    font-size: 18px;
    color: #272c34;
    border-bottom: 1px solid #d5e6e0;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  
  .accommodations-form {
    .form-section {
      background-color: #f8faf9;
      border-radius: 6px;
      padding: 25px;
      margin-bottom: 15px;
      
      .section-title {
        font-weight: 600;
        font-size: 16px;
        color: #272c34;
        margin-bottom: 15px;
      }
      
      .current-date-display {
        background-color: #f8f9fa;
        border: none;
        border-radius: 4px;
        padding: 15px;
        border-left: 4px solid #6c757d;
        
        .form-label {
          font-weight: 500;
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 4px;
          display: block;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .current-date-value {
          font-size: 13px;
          color: #495057;
          font-weight: 500;
        }
      }
      
      .accommodation-explanation {
        .explanation-text {
          font-size: 13px;
          color: #495057;
          margin: 0;
          padding: 12px 15px;
          background-color: #e8f4f8;
          border-radius: 4px;
          border-left: 4px solid #17a2b8;
          line-height: 1.4;
        }
      }
      
      .form-group {
        margin-bottom: 0;
        
        .form-label {
          font-weight: 500;
          font-size: 12px;
          color: #3dbc9e;
          margin-bottom: 6px;
        }
        
        .form-control {
          border-radius: 4px;
          background-color: #ffffff;
          border: 2px solid #e1e1e1;
          font-size: 13px;
          padding: 12px 15px;
          width: 100%;
          transition: border-color 0.2s, box-shadow 0.2s;
          
          &:focus {
            background-color: #ffffff;
            border-color: #3dbc9e;
            outline: 0;
            box-shadow: 0 0 0 3px rgba(61, 188, 158, 0.1);
          }
        }
      }
    }
    
    .form-actions {
      padding-top: 15px;
      border-top: 1px solid #e1e1e1;
      margin-top: 15px;
      
      .btn {
        padding: 10px 20px;
        font-weight: 500;
        font-size: 13px;
        border-radius: 4px;
        min-width: 100px;
        
        &.default {
          background-color: #ffffff;
          border: 1px solid #d1d5db;
          color: #6b7280;
          
          &:hover {
            background-color: #f9fafb;
            border-color: #9ca3af;
          }
        }
        
        &.primary {
          background-color: #3dbc9e;
          border: 1px solid #3dbc9e;
          color: #ffffff;
          
          &:hover {
            background-color: #339e82;
            border-color: #339e82;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .modal-accommodations-content {
    width: 95%;
    margin: 10px auto;
    padding: 15px;
    
    .modal-body {
      padding: 0;
    }
    
    .accommodations-form .form-section {
      padding: 15px;
    }
    
    .form-actions {
      flex-direction: column;
      gap: 8px;
      
      .btn {
        width: 100%;
      }
    }
  }
}
</style>